import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import type {
  CollectionImportChangeDTO,
  TravellerDTO,
} from '@/api/trafikportalen';
import { DialogWindow } from '@/components/Dialog';
import { cn } from '@/helpers/classnames';
import { isValidAddress } from '@/helpers/transformImportData';
import {
  DUPLICATE_NIN_IMPORT_FIELD,
  formatFieldName,
  isFieldRequired,
  translateErrorMessage,
} from './importRowUtils';
import type { ParsedError } from './importRowUtils';
import type { Dispatch, SetStateAction } from 'react';
import Input from '@/components/core/Input';
import CheckBox from '@/components/core/Checkbox';
import Button from '@/components/core/Button';
import { FormElement } from '@/components/FormElement';
import { useTenantID } from '@/hooks/useTenantID';
import { IconExternalLink, IconUserGroup } from '@/icons';
import { STUDENT_TYPE_VIEW } from '@/helpers/collectionTypes';
import type { StudentType } from '@/types/students';
import { Link } from '@tanstack/react-router';

const ReadOnlyValue = ({ value }: { value: string }) => (
  <span className="text-sm text-text-700">{value}</span>
);

const EmptyValue = () => (
  <span className="text-sm italic text-text-500">
    <FormattedMessage defaultMessage="Ej angiven" />
  </span>
);

interface EditableComparisonRowProps {
  field: string;
  existingValue?: string;
  editedValue: string;
  originalValue: string;
  errorMessage?: string;
  onChange: (field: string, value: string) => void;
  hideImported?: boolean;
  required?: boolean;
  studentType: StudentType;
  disableHalfProduct: boolean;
}

const isCheckedValue = (value: string) => {
  const v = value.toLowerCase();
  return v === 'true' || v === '1' || v === 'ja';
};

const formatBooleanDisplay = (value: string): string => {
  return isCheckedValue(value) ? 'Ja' : 'Nej';
};

const EditableComparisonRow = ({
  field,
  existingValue,
  editedValue,
  originalValue,
  errorMessage,
  onChange,
  hideImported,
  required,
  studentType,
  disableHalfProduct,
}: EditableComparisonRowProps) => {
  const { formatMessage } = useIntl();
  const existing = existingValue ?? '';
  const isDifferent = existing !== editedValue && existing !== '';
  const isBoolean = field === 'half_product';
  const isEdited = editedValue !== originalValue;
  const importedValueLabel = isBoolean
    ? formatBooleanDisplay(originalValue)
    : originalValue || '-';
  const helperText = isEdited
    ? formatMessage(
        { defaultMessage: 'Importerat: {value}' },
        { value: importedValueLabel },
      )
    : undefined;

  return (
    <tr
      className={cn('rounded', isDifferent && !hideImported && 'bg-yellow-50')}
    >
      <td className="px-2 py-1 text-sm text-text-700">
        {formatFieldName(field, { studentType, disableHalfProduct })}
        {required && <span className="text-red-600"> *</span>}
      </td>
      <td className="min-w-0 px-2 py-1">
        {existing ? (
          <span
            className={cn(
              'text-sm',
              isDifferent && !hideImported
                ? 'text-red-700 line-through'
                : 'text-text-700',
            )}
          >
            {isBoolean ? formatBooleanDisplay(existing) : existing}
          </span>
        ) : (
          <EmptyValue />
        )}
      </td>
      {!hideImported && (
        <td className="min-w-0 px-2 py-1">
          {isBoolean ? (
            <div className="flex flex-col gap-1">
              <CheckBox
                id={`edit-${field}`}
                checked={isCheckedValue(editedValue)}
                onCheckedChange={(checked) =>
                  onChange(field, String(checked === true))
                }
                aria-label={formatFieldName(field, {
                  studentType,
                  disableHalfProduct,
                })}
              />
              {isEdited && helperText ? (
                <span className="text-xs text-text-500">{helperText}</span>
              ) : null}
            </div>
          ) : (
            <Input
              type="text"
              value={editedValue}
              variant={errorMessage ? 'error' : 'outline'}
              error={errorMessage}
              helperText={helperText}
              helperTextReserveSpace
              onChange={(e) => onChange(field, e.target.value)}
              aria-label={formatFieldName(field, {
                studentType,
                disableHalfProduct,
              })}
            />
          )}
        </td>
      )}
    </tr>
  );
};

export interface ImportRowDetailDialogProps {
  open: boolean;
  onOpenChange: Dispatch<SetStateAction<boolean>>;
  change: CollectionImportChangeDTO;
  travellerData?: TravellerDTO;
  parsedErrors: ParsedError[];
  rowDataObject: Record<string, string>;
  importedFileRowDataObject?: Record<string, string>;
  existingData?: Record<string, string>;
  comparisonFields: string[];
  onSave?: (rowNumber: number, editedData: Record<string, string>) => void;
  isSaving?: boolean;
  studentType: StudentType;
  disableHalfProduct?: boolean;
  onShowDuplicateNin?: (nin: string) => void;
}

export const ImportRowDetailDialog = ({
  open,
  onOpenChange,
  change,
  travellerData,
  parsedErrors,
  rowDataObject,
  importedFileRowDataObject,
  existingData,
  comparisonFields,
  onSave,
  isSaving = false,
  studentType,
  disableHalfProduct = false,
  onShowDuplicateNin,
}: ImportRowDetailDialogProps) => {
  const intl = useIntl();
  const tenantId = useTenantID();
  const fileOriginalRow = importedFileRowDataObject ?? rowDataObject;
  const isRemoved = change.action === 'removed';
  const showComparison = !!existingData && Object.keys(existingData).length > 0;

  const duplicateNin = useMemo(() => {
    const hasDuplicateNinError = parsedErrors.some(
      (error) => error.field.toLowerCase() === DUPLICATE_NIN_IMPORT_FIELD,
    );
    if (!hasDuplicateNinError) return undefined;
    return (
      rowDataObject.national_identity_number?.trim() ||
      fileOriginalRow.national_identity_number?.trim() ||
      undefined
    );
  }, [parsedErrors, rowDataObject, fileOriginalRow]);

  const [editedData, setEditedData] = useState<Record<string, string>>(() => ({
    ...rowDataObject,
  }));

  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setEditedData({ ...rowDataObject });
    }
    prevOpenRef.current = open;
  }, [open, rowDataObject]);

  const validationErrorsRef = useRef<HTMLDivElement>(null);
  const errorsSignature = parsedErrors
    .map((error) => `${error.field}:${error.message}`)
    .join('|');
  const seenErrorsSignature = useRef<string | null>(null);

  useEffect(() => {
    if (!open) {
      seenErrorsSignature.current = null;
      return;
    }

    const previous = seenErrorsSignature.current;
    seenErrorsSignature.current = errorsSignature;
    if (previous === null || previous === errorsSignature || !errorsSignature) {
      return;
    }

    validationErrorsRef.current?.focus();
  }, [open, errorsSignature]);

  const serverErrorsByField = useMemo(() => {
    const map: Record<string, string> = {};
    for (const error of parsedErrors) {
      const key = error.field.toLowerCase();
      map[key] = translateErrorMessage(intl, error);
    }
    return map;
  }, [parsedErrors, intl]);

  const localValidationErrors = useMemo(() => {
    if (isRemoved) return {};

    const errors: Record<string, string> = {};
    const requiredMsg = intl.formatMessage({
      defaultMessage: 'Obligatoriskt fält',
    });
    const addressOrStopMsg = intl.formatMessage({
      defaultMessage: 'Adress eller hållplats krävs',
    });
    const incompleteAddressMsg = intl.formatMessage({
      defaultMessage: 'Alla adressfält krävs om adress anges',
    });

    for (const field of comparisonFields) {
      if (field === 'half_product') continue;
      if (
        isFieldRequired(field, studentType, disableHalfProduct) &&
        !editedData[field]?.trim()
      ) {
        errors[field] = requiredMsg;
      }
    }

    if (studentType !== 'child-and-youth') {
      const addressGroups = [
        {
          prefix: 'guardian1_address',
          busStop: 'guardian1_bus_stop',
          email: 'guardian1_email',
          alwaysValidate: true,
        },
        {
          prefix: 'guardian2_address',
          busStop: 'guardian2_bus_stop',
          email: 'guardian2_email',
          alwaysValidate: false,
        },
      ];

      for (const { prefix, busStop, email, alwaysValidate } of addressGroups) {
        const addressFields = [
          `${prefix}.street`,
          `${prefix}.zip_code`,
          `${prefix}.city`,
        ];
        const addressInputFields = [...addressFields, `${prefix}.care_of`];
        const allGroupFields = [...addressInputFields, busStop, email];

        if (
          !alwaysValidate &&
          !allGroupFields.some((f) => editedData[f]?.trim())
        ) {
          continue;
        }

        const addressObj = {
          street: editedData[`${prefix}.street`] ?? '',
          zip_code: editedData[`${prefix}.zip_code`] ?? '',
          city: editedData[`${prefix}.city`] ?? '',
        };
        const validAddress = isValidAddress(addressObj);
        const hasBusStop = !!editedData[busStop]?.trim();
        const hasAnyAddressField = addressInputFields.some((f) =>
          editedData[f]?.trim(),
        );

        if (!validAddress && !hasBusStop) {
          if (hasAnyAddressField) {
            for (const f of addressFields) {
              if (!editedData[f]?.trim() && !errors[f]) {
                errors[f] = incompleteAddressMsg;
              }
            }
          } else {
            for (const f of addressFields) {
              if (!errors[f]) errors[f] = addressOrStopMsg;
            }
            if (!errors[busStop]) errors[busStop] = addressOrStopMsg;
          }
        } else if (hasAnyAddressField && !validAddress) {
          for (const f of addressFields) {
            if (!editedData[f]?.trim() && !errors[f]) {
              errors[f] = incompleteAddressMsg;
            }
          }
        }
      }
    }

    return errors;
  }, [
    editedData,
    comparisonFields,
    studentType,
    intl,
    isRemoved,
    disableHalfProduct,
  ]);

  const errorsByField = useMemo(
    () => ({ ...serverErrorsByField, ...localValidationErrors }),
    [localValidationErrors, serverErrorsByField],
  );

  const hasLocalErrors = Object.keys(localValidationErrors).length > 0;

  const handleFieldChange = useCallback((field: string, value: string) => {
    setEditedData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const hasChanges = useMemo(() => {
    return comparisonFields.some(
      (field) => (editedData[field] ?? '') !== (rowDataObject[field] ?? ''),
    );
  }, [editedData, rowDataObject, comparisonFields]);

  const handleSave = useCallback(() => {
    if (hasLocalErrors) return;
    onSave?.(change.row_number, editedData);
  }, [onSave, change.row_number, editedData, hasLocalErrors]);

  const handleReset = useCallback(() => {
    setEditedData({ ...rowDataObject });
  }, [rowDataObject]);

  const handleShowDuplicateNin = useCallback(() => {
    if (!duplicateNin) return;
    onShowDuplicateNin?.(duplicateNin);
    onOpenChange(false);
  }, [duplicateNin, onShowDuplicateNin, onOpenChange]);

  return (
    <DialogWindow
      open={open}
      onOpenChange={onOpenChange}
      title={
        isRemoved
          ? intl.formatMessage({ defaultMessage: 'Radinformation' })
          : intl.formatMessage(
              { defaultMessage: 'Radinformation - Rad {rowNumber}' },
              { rowNumber: change.row_number },
            )
      }
      content={
        <div className="flex flex-col gap-4">
          {change.traveller_id && change.action !== 'added' && (
            <div className="flex justify-end">
              <Link
                to="/$tenantId/travellers/$travellerId/$view"
                params={{
                  tenantId,
                  travellerId: change.traveller_id,
                  view: STUDENT_TYPE_VIEW[studentType],
                }}
                target="_blank"
                className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-800"
              >
                <FormattedMessage defaultMessage="Öppna resenär" />
                <IconExternalLink aria-hidden="true" />
                <span className="sr-only">
                  <FormattedMessage defaultMessage="(öppnas i nytt fönster)" />
                </span>
              </Link>
            </div>
          )}

          {travellerData && !showComparison && (
            <div className="rounded-md border border-primary-300 bg-primary-50 p-4">
              <h3 className="mb-2 font-semibold text-primary-900">
                <FormattedMessage defaultMessage="Befintlig resenär" />
              </h3>
              <div className="flex flex-col gap-1">
                <FormElement
                  label={<FormattedMessage defaultMessage="Namn" />}
                  input={
                    <ReadOnlyValue
                      value={`${travellerData.first_name} ${travellerData.last_name}`}
                    />
                  }
                />
                <FormElement
                  label={<FormattedMessage defaultMessage="Personnummer" />}
                  visible={!!travellerData.national_identity_number}
                  input={
                    <ReadOnlyValue
                      value={travellerData.national_identity_number ?? ''}
                    />
                  }
                />
                <FormElement
                  label={<FormattedMessage defaultMessage="E-post" />}
                  visible={!!travellerData.email}
                  input={<ReadOnlyValue value={travellerData.email ?? ''} />}
                />
              </div>
            </div>
          )}

          {change.action === 'removed' ? (
            <p className="text-text-900">
              <FormattedMessage defaultMessage="Resenären fanns inte med i filen men finns redan i systemet: kommer inaktiveras." />
            </p>
          ) : null}

          {change.action === 'confirmed' && (
            <p className="text-text-900">
              <FormattedMessage defaultMessage="Resenären är manuellt redigerad men datan i importen är samma, vilket är bra." />
            </p>
          )}

          {parsedErrors.length > 0 && (
            <div
              ref={validationErrorsRef}
              tabIndex={-1}
              aria-live="polite"
              aria-atomic="true"
              className="rounded-md border border-red-300 bg-red-50 p-4 outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-red-700"
            >
              <h3 className="mb-2 font-semibold text-red-900">
                <FormattedMessage defaultMessage="Valideringsfel" />
              </h3>
              <div className="flex flex-col gap-1">
                {parsedErrors.map((error, index) => (
                  <FormElement
                    key={index}
                    label={
                      <span className="text-red-800">
                        {formatFieldName(error.field.toLowerCase(), {
                          studentType,
                          disableHalfProduct,
                        })}
                      </span>
                    }
                    input={
                      <span className="text-sm text-red-700">
                        {translateErrorMessage(intl, error)}
                      </span>
                    }
                  />
                ))}
              </div>
            </div>
          )}

          {duplicateNin && onShowDuplicateNin && (
            <div className="flex justify-start">
              <Button
                variant="outline"
                size="sm"
                startIcon={<IconUserGroup aria-hidden="true" />}
                onClick={handleShowDuplicateNin}
              >
                <FormattedMessage defaultMessage="Visa rader med samma personnummer" />
              </Button>
            </div>
          )}

          {showComparison ? (
            <div className="rounded-md border border-background-300 bg-background-100 p-4">
              <h3 className="mb-3 font-semibold text-text-900">
                {isRemoved ? (
                  <FormattedMessage defaultMessage="Befintlig data" />
                ) : (
                  <FormattedMessage defaultMessage="Jämförelse" />
                )}
              </h3>
              <table className="w-full">
                <caption className="sr-only">
                  {isRemoved
                    ? intl.formatMessage({
                        defaultMessage: 'Befintlig data för raden',
                      })
                    : intl.formatMessage({
                        defaultMessage:
                          'Jämförelse mellan befintlig och importerad data',
                      })}
                </caption>
                <thead>
                  <tr className="border-b border-background-300">
                    <th
                      scope="col"
                      className="px-2 pb-2 text-left text-xs font-medium uppercase tracking-wide text-text-500"
                    >
                      <FormattedMessage defaultMessage="Fält" />
                    </th>
                    <th
                      scope="col"
                      className="px-2 pb-2 text-left text-xs font-medium uppercase tracking-wide text-text-500"
                    >
                      <FormattedMessage defaultMessage="Befintligt" />
                    </th>
                    {!isRemoved && (
                      <th
                        scope="col"
                        className="px-2 pb-2 text-left text-xs font-medium uppercase tracking-wide text-text-500"
                      >
                        <FormattedMessage defaultMessage="Importerad" />
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {comparisonFields.map((field) => (
                    <EditableComparisonRow
                      key={field}
                      field={field}
                      existingValue={existingData[field]}
                      editedValue={editedData[field] ?? ''}
                      originalValue={fileOriginalRow[field] ?? ''}
                      errorMessage={errorsByField[field]}
                      onChange={handleFieldChange}
                      hideImported={isRemoved}
                      required={isFieldRequired(
                        field,
                        studentType,
                        disableHalfProduct,
                      )}
                      studentType={studentType}
                      disableHalfProduct={disableHalfProduct}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          ) : !isRemoved ? (
            <div className="rounded-md border border-background-300 bg-background-100 p-4">
              <h3 className="mb-2 font-semibold text-text-900">
                <FormattedMessage defaultMessage="Inmatad data" />
              </h3>
              <div className="flex flex-col gap-2">
                {comparisonFields.map((field) => {
                  const fieldLabel = isFieldRequired(
                    field,
                    studentType,
                    disableHalfProduct,
                  )
                    ? `${formatFieldName(field, { studentType, disableHalfProduct })} *`
                    : formatFieldName(field, {
                        studentType,
                        disableHalfProduct,
                      });
                  const originalValue = fileOriginalRow[field] ?? '';
                  const isEdited = (editedData[field] ?? '') !== originalValue;
                  const editedHelperText = isEdited
                    ? intl.formatMessage(
                        { defaultMessage: 'Importerat: {value}' },
                        {
                          value:
                            field === 'half_product'
                              ? formatBooleanDisplay(originalValue)
                              : originalValue || '-',
                        },
                      )
                    : undefined;
                  const inputId = `edit-plain-${field}`;
                  return field === 'half_product' ? (
                    <FormElement
                      key={field}
                      id={inputId}
                      label={fieldLabel}
                      input={
                        <div className="flex flex-col gap-1">
                          <CheckBox
                            id={inputId}
                            checked={isCheckedValue(editedData[field] ?? '')}
                            onCheckedChange={(checked) =>
                              handleFieldChange(field, String(checked === true))
                            }
                            aria-label={formatFieldName(field, {
                              studentType,
                              disableHalfProduct,
                            })}
                          />
                          {isEdited && editedHelperText ? (
                            <span className="text-xs text-text-500">
                              {editedHelperText}
                            </span>
                          ) : null}
                        </div>
                      }
                    />
                  ) : (
                    <FormElement
                      key={field}
                      id={inputId}
                      label={fieldLabel}
                      input={
                        <Input
                          id={inputId}
                          type="text"
                          value={editedData[field] ?? ''}
                          variant={errorsByField[field] ? 'error' : 'outline'}
                          error={errorsByField[field]}
                          helperText={editedHelperText}
                          helperTextReserveSpace
                          onChange={(e) =>
                            handleFieldChange(field, e.target.value)
                          }
                        />
                      }
                    />
                  );
                })}
              </div>
            </div>
          ) : null}

          {change.action !== 'removed' && (
            <div className="flex justify-end gap-2 border-t border-background-300 pt-4">
              <Button
                variant="outline"
                onClick={handleReset}
                disabled={!hasChanges || isSaving}
              >
                <FormattedMessage defaultMessage="Återställ" />
              </Button>
              <Button
                variant="filled"
                onClick={handleSave}
                disabled={!hasChanges || hasLocalErrors}
                loading={isSaving}
              >
                <FormattedMessage defaultMessage="Spara och validera" />
              </Button>
            </div>
          )}
        </div>
      }
    />
  );
};
